/**
 * Keep a Map used as a cache from growing without bound.
 *
 * Most of the app's small caches (keyed by project, company, account, URL)
 * drop an entry only when that same key is read again after its time is up,
 * so a key nobody asks for again stays for the life of the server instance or
 * the browser tab — and both run for days. Call this after each write: once
 * the map is past `max`, it first drops every entry `isStale` says is spent,
 * then the oldest writes (a Map iterates in insertion order) until it is back
 * to `max`. Under the cap it does nothing, so a cache's behaviour is
 * unchanged until it would otherwise have grown past it.
 */
export function capMap<K, V>(map: Map<K, V>, max: number, isStale?: (value: V, key: K) => boolean): void {
  if (map.size <= max) return;
  if (isStale) {
    for (const [key, value] of map) if (isStale(value, key)) map.delete(key);
  }
  for (const key of map.keys()) {
    if (map.size <= max) break;
    map.delete(key);
  }
}
